import { selectParticipantRowHidden } from "../../room/state/stage";
import { useLayoutEffect, useRef } from "react";
import { useRoomState, useRoomController } from "../../room/RoomContext";
import { FocusedStreamOverlays } from "./FocusedStreamOverlays";
import { StageControls, type DockRoom } from "./StageControls";
import { ParticipantCloud } from "./ParticipantCloud";
import { StreamGrid } from "./StreamGrid";
import { StreamContextMenu } from "./StreamContextMenu";
import { StageToasts } from "./StageToasts";
import { useTranslation } from "react-i18next";
import type { AuthUser } from "../../auth/auth";
import { ArrowLeft } from "lucide-react";
import { Button } from "../ui/button";

function EmptyStage() {
  const { t } = useTranslation();

  const visible = useRoomState((s) => s.stage.emptyStageVisible);
  return (
    <div
      id="emptyStage"
      hidden={!visible}
      className="absolute inset-0 grid place-content-center justify-items-center text-center text-muted-foreground"
    >
      <div className="mb-3.5 grid size-16 place-items-center rounded-3xl bg-zinc-900 text-3xl">
        ▦
      </div>
      <h2 className="mb-1.5 font-heading text-base font-bold text-foreground">
        {t("stage.waitingTitle")}
      </h2>
      <p className="text-xs">{t("stage.waitingDescription")}</p>
    </div>
  );
}

export function Stage({
  room,
  user,
  onSignOut,
  onBack,
}: {
  room: DockRoom;
  user: AuthUser | null;
  onSignOut: () => void;
  onBack: () => void;
}) {
  const { t } = useTranslation();

  const stageRef = useRef<HTMLElement>(null);
  const focusActive = useRoomState((s) => s.stage.streamFocusActive);
  const overlaysVisible = useRoomState((s) => s.stage.focusedOverlaysVisible);
  const rowHidden = useRoomState(selectParticipantRowHidden);
  const hasCamera = useRoomState((s) =>
    s.participants.participantCloud.some((person) => person.camera),
  );
  const controller = useRoomController();
  const count = useRoomState((s) => s.participants.peopleCount);
  const summary = useRoomState((s) => s.connection.connectionSummary);
  const eventLog = useRoomState((s) => s.log.eventLog);
  useLayoutEffect(() => {
    controller.registerOverlayUsage(() =>
      [
        ...(stageRef.current?.querySelectorAll<HTMLElement>(
          "[data-focused-overlay-use], [data-participants-toggle]",
        ) || []),
      ].some((element) =>
        element.matches(
          element.dataset.focusedOverlayUse === "close"
            ? ":hover, :focus-visible"
            : ":hover, :focus-within",
        ),
      ),
    );
    return () => {
      controller.registerOverlayUsage(null);
      // An open quality popover would otherwise reopen on the away dock.
      controller.setQualityPopoverOpen("host", false);
      controller.setQualityPopoverOpen("viewer", false);
    };
  }, [controller]);
  const revealOverlay = (event: React.SyntheticEvent) => {
    if (
      (event.target as Element).closest("[data-focused-stream-overlay], [data-participants-toggle]")
    )
      controller.revealFocusedOverlays();
  };
  return (
    <>
      {/* eslint-disable react-hooks/refs -- registerStageShell is a stable controller callback ref, not a ref-object read; the rule cannot tell without types, and wrapping it would re-register the shell on every render */}
      <main
        ref={controller.registerStageShell}
        className="stage-shell grid h-full min-h-0 w-full min-w-0 grid-rows-[minmax(0,1fr)_auto] bg-black"
      >
        <section
          ref={stageRef}
          id="stageWrap"
          onPointerMove={revealOverlay}
          onFocus={revealOverlay}
          data-focus-active={focusActive || undefined}
          data-overlays-visible={overlaysVisible || undefined}
          data-focus-layout={
            !focusActive ? undefined : rowHidden ? "hidden" : hasCamera ? "camera" : undefined
          }
          className="stage-wrap group/stage relative min-h-0 min-w-0 overflow-hidden bg-black px-[clamp(0.75rem,8vw,4.75rem)] pt-2.5 pb-1.5 before:pointer-events-none before:absolute before:inset-x-0 before:top-0 before:z-10 before:h-26 before:bg-[linear-gradient(to_bottom,color-mix(in_srgb,black_78%,transparent),color-mix(in_srgb,black_38%,transparent)_55%,transparent)] before:opacity-0 before:transition-opacity before:duration-160 before:content-[''] group-data-[focus-active]/stage:group-data-[overlays-visible]/stage:before:opacity-100 max-[53.75rem]:px-4.5 max-[40.625rem]:p-2"
        >
          <span id="peopleCount" className="sr-only">
            {t("common.person", { count })}
          </span>
          {/* Leaves the stage only; the call keeps running (see CallBar). */}
          <Button
            variant="secondary"
            size="icon-sm"
            className="absolute top-3 left-3 z-30 rounded-full bg-card shadow-md hover:bg-card/80 max-[40.625rem]:top-2 max-[40.625rem]:left-2"
            type="button"
            aria-label={t("home.back")}
            title={t("home.back")}
            onClick={onBack}
          >
            <ArrowLeft aria-hidden="true" />
          </Button>
          <FocusedStreamOverlays />
          <div id="connectionSummary" className="sr-only" aria-live="polite">
            {summary || t("connection.state.offline")}
          </div>
          <EmptyStage />
          <ParticipantCloud />
          <StreamGrid />
        </section>
        <StageControls room={room} user={user} onSignOut={onSignOut} />
        <StreamContextMenu />
        <pre id="eventLog" hidden>
          {eventLog}
        </pre>
      </main>
      {/* eslint-enable react-hooks/refs */}
      <StageToasts />
    </>
  );
}
